/**
 * The DeArrow wire format, measured against the live service (2026-08-02) rather than assumed:
 *
 * - `GET {base}/api/branding/{sha256-prefix}` answers an **object keyed by `videoID`** (~150 keys
 *   for a 4-hex prefix) — not SponsorBlock's array-of-rows shape, though the hashing is the same.
 * - An entry is `{ titles: [...], thumbnails: [...], casualVotes, randomTime, videoDuration }`.
 *   A title row is `{ title, original, votes, locked, UUID }`; a thumbnail row is
 *   `{ timestamp, original, votes, locked, UUID }` where `timestamp` is fractional seconds and
 *   **`null` on an `original: true` row** (the row *is* the vote for the original picture).
 * - A missing video is the normal case for ~149 of the ~150 bucket entries, and means "no
 *   submissions" — the caller renders the record's own title and thumbnail.
 *
 * Total (R4): a malformed row drops itself; a malformed payload answers "nothing stands".
 */

/** What the community chose for one video — each half `null` where the original stands. */
export interface DeArrowBranding {
  /** The replacement title, cleaned for display, or `null`. */
  readonly title: string | null;
  /** The generated thumbnail's timestamp (fractional seconds), or `null`. */
  readonly thumbnailTimestampSec: number | null;
}

interface SubmissionRow {
  readonly original: boolean;
  readonly votes: number;
  readonly locked: boolean;
}

/**
 * The reference client's reading, shared by both halves: rows voted below zero are out unless a
 * moderator locked them, locked rows outrank everything, then votes decide. The winner being an
 * `original: true` row is a real answer — the community voted *for* what YouTube shows — and maps
 * to `null` exactly like no submissions at all.
 */
function best<Row extends SubmissionRow>(rows: readonly Row[]): Row | null {
  const eligible = rows.filter((row) => row.locked || row.votes >= 0);
  if (eligible.length === 0) return null;
  return [...eligible].sort((a, b) => {
    if (a.locked !== b.locked) return a.locked ? -1 : 1;
    return b.votes - a.votes;
  })[0] ?? null;
}

/**
 * A leading `>` is DeArrow's "do not auto-format" marker, part of the stored string on many real
 * submissions. Visious never auto-formats, so the marker's only effect here would be a stray
 * bracket on screen — stripped once, at the boundary.
 */
function cleanTitle(title: string): string {
  return title.replace(/^>\s*/, '').trim();
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

interface TitleRow extends SubmissionRow {
  readonly title: string;
}

function parseTitleRows(rows: unknown): readonly TitleRow[] {
  if (!Array.isArray(rows)) return [];
  const out: TitleRow[] = [];
  for (const row of rows) {
    if (!isRecord(row)) continue;
    const title = row['title'];
    const votes = row['votes'];
    if (typeof title !== 'string' || title.trim() === '') continue;
    if (typeof votes !== 'number' || !Number.isFinite(votes)) continue;
    out.push({
      title,
      votes,
      original: row['original'] === true,
      locked: row['locked'] === true,
    });
  }
  return out;
}

interface ThumbnailRow extends SubmissionRow {
  /** `null` on the `original: true` row — the row is a vote, not a picture. */
  readonly timestampSec: number | null;
}

function parseThumbnailRows(rows: unknown): readonly ThumbnailRow[] {
  if (!Array.isArray(rows)) return [];
  const out: ThumbnailRow[] = [];
  for (const row of rows) {
    if (!isRecord(row)) continue;
    const votes = row['votes'];
    if (typeof votes !== 'number' || !Number.isFinite(votes)) continue;
    const original = row['original'] === true;
    const timestamp = row['timestamp'];
    const timestampSec =
      typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp >= 0
        ? timestamp
        : null;
    // A non-original row without a usable timestamp names no picture and cannot win anything.
    if (!original && timestampSec === null) continue;
    out.push({ timestampSec, votes, original, locked: row['locked'] === true });
  }
  return out;
}

/**
 * The community's answer for `videoId` out of a bucket response, or "nothing stands" — one object
 * either way, so the caller never distinguishes "absent" from "present but voted down".
 */
export function parseDeArrowBucket(payload: unknown, videoId: string): DeArrowBranding {
  const nothing: DeArrowBranding = { title: null, thumbnailTimestampSec: null };
  if (!isRecord(payload)) return nothing;
  const entry = payload[videoId];
  if (!isRecord(entry)) return nothing;

  const title = best(parseTitleRows(entry['titles']));
  const thumbnail = best(parseThumbnailRows(entry['thumbnails']));

  return {
    title: title === null || title.original ? null : cleanTitle(title.title) || null,
    thumbnailTimestampSec: thumbnail === null || thumbnail.original ? null : thumbnail.timestampSec,
  };
}
